import type { ActResult, CasinoGame, TableEnv } from './types';
import { STEP_BASIS, luckOf, luckTarget, stepSaveRate } from './luck';

/**
 * Mines: a 4x4 board hides some mines. Each safe tile you reveal raises the
 * payout; hit a mine and the bet is gone. Cash out any time.
 *
 * While a board is live the game takes over the gestures: scroll moves the
 * cursor across the board (row by row), tap reveals, double-tap cashes out.
 *
 * Payout after k safe picks is the fair odds of surviving k picks times
 * (1 - EDGE), so every stopping point returns the same (1 - EDGE) on
 * average. Checked in odds.test.ts.
 */
export const EDGE = 0.03;
/** Mines gets part of the ★'s luck: two safe picks on a low-mine board
 *  barely swing, so the full boost made a Mines ★ a near-certain win
 *  (100% of sim runs). It's still a lucky floor, just not a sure thing. */
/** (An object so the balance sims can try other shares.) */
export const MINES_LUCK = { share: 0.5 };

/** Mines' target return at a luck level (its share of the ★'s boost). */
export function minesTarget(luck: number): number {
  return 1 + (luckTarget(luck) - 1) * MINES_LUCK.share;
}
/** Clearing every safe tile with at least this many mines wins the jackpot. */
export const JACKPOT_MIN_MINES = 5;
export const SIZE = 4;
export const CELLS = SIZE * SIZE;
export const MINE_COUNTS = [1, 3, 5, 8];

/** Payout multiple after `safe` safe reveals with `mines` mines on the board. */
export function minesMultiplier(mines: number, safe: number, edge: number = EDGE): number {
  let fair = 1;
  for (let i = 0; i < safe; i++) fair *= (CELLS - i) / (CELLS - mines - i);
  return safe === 0 ? 1 : fair * (1 - edge);
}

export interface MinesState {
  phase: 'idle' | 'playing' | 'done';
  countIndex: number;
  mineCells: number[];
  /** Edge locked in when the board started (House Special or not). */
  edge: number;
  revealed: boolean[];
  cursor: number;
  bet: number;
  /** Tile that blew up, if the round ended on a mine. */
  boom: number | null;
  /** The table's luck when the round started (★ luck turns mines to duds). */
  luck?: number;
  /** A mine turned out to be a dud this round. */
  dud?: boolean;
  returned: number;
}

function mineCount(state: MinesState): number {
  return MINE_COUNTS[state.countIndex] ?? 3;
}

function safeCount(state: MinesState): number {
  return state.revealed.filter(Boolean).length;
}

function payout(state: MinesState, safe: number): number {
  return Math.round(state.bet * minesMultiplier(state.mineCells.length, safe, state.edge ?? EDGE));
}

function cashOut(state: MinesState, bankroll: number): ActResult<MinesState> {
  const safe = safeCount(state);
  const returned = payout(state, safe);
  const cleared = safe === CELLS - state.mineCells.length;
  return {
    state: { ...state, phase: 'done', returned, boom: null },
    bankroll: bankroll + returned,
    // Cashing out before any tile risked nothing: not a play (see highLow).
    settled: safe === 0 ? undefined : { wagered: state.bet, returned },
    jackpot: cleared && state.mineCells.length >= JACKPOT_MIN_MINES,
  };
}

function reveal(state: MinesState, env: TableEnv): ActResult<MinesState> {
  const cell = state.cursor;
  if (state.revealed[cell]) return { state, bankroll: env.bankroll };
  if (state.mineCells.includes(cell)) {
    // The ★'s luck: sometimes a mine is a dud. It moves to another hidden
    // safe tile (so the count stays right) and this tile is safe after all.
    const hidden = state.revealed.map((r, i) => (r ? -1 : i)).filter((i) => i >= 0);
    const safeHidden = hidden.filter((i) => !state.mineCells.includes(i));
    const ratio = Math.sqrt(minesTarget(state.luck ?? 0) / (1 - (state.edge ?? EDGE)));
    // No luck, no duds (a normal table): the ratio alone would still come out
    // above 1, since it aims for 100% from a 97% table.
    const q = (state.luck ?? 0) > 0 ? stepSaveRate(safeHidden.length / hidden.length, ratio ** (2 / STEP_BASIS)) : 0;
    if (safeHidden.length > 1 && env.rng.next() < q) {
      // (The tapped tile is a mine, so it isn't in safeHidden.)
      const to = safeHidden[env.rng.int(safeHidden.length)]!;
      const mineCells = state.mineCells.map((c) => (c === cell ? to : c)).sort((a, b) => a - b);
      // A fizzle on the tile, then the play goes on as if it were safe.
      const safe = reveal({ ...state, mineCells, dud: true }, env);
      const fizzle = (c: string, line: string) => `${board({ ...state, cursor: -1 }, { [cell]: c })}\n\n${line}`;
      return { ...safe, frames: [fizzle('!', 'Click...'), fizzle('~', '★ A dud! Safe after all.'), ...(safe.frames ?? [])] };
    }
    const done: MinesState = { ...state, phase: 'done', boom: cell, returned: 0 };
    return {
      state: done,
      bankroll: env.bankroll,
      settled: { wagered: state.bet, returned: 0 },
      frames: boomFrames(state, cell),
    };
  }
  const revealed = [...state.revealed];
  revealed[cell] = true;
  let next: MinesState = { ...state, revealed };
  // Hop the cursor to the next hidden tile so repeated taps keep going.
  for (let step = 1; step < CELLS; step++) {
    const candidate = (cell + step) % CELLS;
    if (!revealed[candidate]) {
      next = { ...next, cursor: candidate };
      break;
    }
  }
  // Every safe tile found: nothing left to risk, so it pays out.
  if (safeCount(next) === CELLS - next.mineCells.length) return cashOut(next, env.bankroll);
  return { state: next, bankroll: env.bankroll };
}

/** The board; `marks` overrides what some tiles show (for animations). */
/** The mine going off: a flash on the tile, the blast reaching its
 *  neighbours, then smoke. */
function boomFrames(state: MinesState, cell: number): string[] {
  const r = Math.floor(cell / SIZE);
  const c = cell % SIZE;
  const around = [-1, 0, 1].flatMap((dr) => [-1, 0, 1].map((dc) => [r + dr, c + dc] as const)).filter(([y, x]) => y >= 0 && x >= 0 && y < SIZE && x < SIZE && !(y === r && x === c)).map(([y, x]) => y * SIZE + x);
  const view = { ...state, cursor: -1 };
  const blast = (tile: string, ring: string) => Object.fromEntries([[cell, tile], ...around.map((i) => [i, ring] as const)]);
  return [
    `${board(view, { [cell]: '!' })}\n\nClick...`,
    `${board(view, blast('#', '*'))}\n\n  B O O M !`,
    `${board(view, blast('X', '~'))}\n\n  B O O M !`,
  ];
}

function board(state: MinesState, marks: Record<number, string> = {}): string {
  const rows: string[] = [];
  for (let r = 0; r < SIZE; r++) {
    const cells: string[] = [];
    for (let c = 0; c < SIZE; c++) {
      const i = r * SIZE + c;
      const shown =
        marks[i] ?? (state.boom === i ? 'X' : state.revealed[i] ? '$' : state.phase === 'done' && state.mineCells.includes(i) ? '*' : '?');
      cells.push(state.phase === 'playing' && state.cursor === i ? `[${shown}]` : ` ${shown} `);
    }
    rows.push(`  ${cells.join(' ')}`);
  }
  return rows.join('\n');
}

export const mines: CasinoGame<MinesState> = {
  id: 'mines',
  noDealer: true,
  usesLuck: true,
  name: 'Mines',
  blurb: 'Find safe tiles; each one raises the payout.',
  baseMinBet: 5,
  baseMaxBet: 100,
  lockedHint: '• reveal  •• cash out',
  specialPerk: 'Luck: now and then a mine is a dud.',
  jackpotRule: 'Clear the board with 5+ mines.',
  create: () => ({ phase: 'idle', countIndex: 1, mineCells: [], edge: EDGE, revealed: [], cursor: 0, bet: 0, boom: null, returned: 0 }),
  locked: (state) => state.phase === 'playing',
  options(state) {
    return [
      { id: 'start', label: state.phase === 'done' ? 'Play again' : 'Start' },
      { id: 'count', label: `Mines: ${mineCount(state)}` },
    ];
  },
  describe(state, env, highlighted) {
    const count = state.phase === 'idle' ? mineCount(state) : state.mineCells.length;
    const idleEdge = EDGE;
    if (highlighted === 'count' && state.phase !== 'playing') {
      const m = mineCount(state);
      return `${m} mine${m === 1 ? '' : 's'} on 16 tiles.\n\nFirst safe pick: ${minesMultiplier(m, 1, idleEdge).toFixed(2)}x\nThree safe: ${minesMultiplier(m, 3, idleEdge).toFixed(2)}x\n\nTap to change.`;
    }
    if (state.phase === 'playing') {
      const safe = safeCount(state);
      const now = safe === 0 ? 'Cash out: bet back' : `Cash out: $${payout(state, safe)}`;
      return `${count} mines · ${safe} safe\n${now}\nNext: ${minesMultiplier(count, safe + 1, state.edge ?? EDGE).toFixed(2)}x ($${payout(state, safe + 1)})`;
    }
    if (state.phase === 'done') {
      const net = state.returned - state.bet;
      const result = state.boom !== null ? `BOOM. -$${state.bet}` : net > 0 ? `${state.dud ? '★ ' : ''}Cashed out +$${net}` : 'Cashed out, broke even';
      return `${board(state)}\n\n${result}`;
    }
    return `Pick safe tiles, cash out before you hit a mine.\n\nMore mines = bigger payouts per pick.`;
  },
  act(state, optionId, env) {
    if (state.phase === 'playing') return { state, bankroll: env.bankroll };
    if (optionId === 'count') return { state: { ...state, countIndex: (state.countIndex + 1) % MINE_COUNTS.length }, bankroll: env.bankroll };
    if (optionId !== 'start') return { state, bankroll: env.bankroll };
    const cells = Array.from({ length: CELLS }, (_, i) => i);
    const mineCells = env.rng.shuffle(cells).slice(0, mineCount(state)).sort((a, b) => a - b);
    return {
      state: { ...state, phase: 'playing', mineCells, edge: EDGE, luck: luckOf(env), dud: false, revealed: Array(CELLS).fill(false), cursor: 0, bet: env.bet, boom: null, returned: 0 },
      bankroll: env.bankroll - env.bet,
    };
  },
  gesture(state, gesture, env) {
    if (state.phase !== 'playing') return null;
    if (gesture === 'up' || gesture === 'down') {
      const step = gesture === 'up' ? -1 : 1;
      return { state: { ...state, cursor: (state.cursor + step + CELLS) % CELLS }, bankroll: env.bankroll };
    }
    if (gesture === 'tap') return reveal(state, env);
    return cashOut(state, env.bankroll);
  },
  leftPane: (state) => (state.phase === 'playing' ? board(state) : null),
};
